import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Card, Heading, Separator, toast } from '@wit-support/ui';
import { Actions, SubmitButton, ZodField, ZodForm } from '@wit-support/ui-forms';
import { env } from '../env.js';
import { postSignInAction } from '../lib/return-to.js';
import { API_ORIGIN } from '../lib/api-origin.js';

const LoginSchema = z.object({
  email: z.string().email().meta({ label: 'Email', placeholder: 'you@example.com' }),
  password: z.string().min(1).meta({ label: 'Password', secret: true }),
});
type LoginInput = z.infer<typeof LoginSchema>;

export interface LoginPageProps {
  /**
   * Signed authorize request from the MCP OAuth flow (`/oauth/login`). When
   * set, it rides along with the credentials so the server resumes the
   * authorize request, and the browser follows the returned continuation
   * (consent page or the client's redirect URI) instead of opening the panel.
   */
  oauthQuery?: string;
  /** Subtitle override for the OAuth context. */
  subtitle?: string;
}

export function LoginPage({ oauthQuery, subtitle }: LoginPageProps = {}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(values: unknown) {
    const data = values as LoginInput;
    setError(null);
    try {
      const r = await fetch(`${env.VITE_ADMIN_API_URL}/auth/sign-in/email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          ...(oauthQuery ? { oauth_query: oauthQuery } : {}),
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}) as { message?: string; error?: string });
        const msg = body?.message ?? body?.error ?? `Login failed (${r.status})`;
        setError(msg);
        toast.error(msg);
        return;
      }
      // MCP OAuth (`oauthQuery`) wins; otherwise a pending, re-validated
      // `returnTo` (the MCP docs on admin-api); otherwise the panel itself.
      const next = postSignInAction({
        oauthQuery,
        body: oauthQuery ? await r.json().catch(() => null) : null,
        apiOrigin: API_ORIGIN,
      });
      if (next.kind === 'assign') {
        window.location.assign(next.url);
        return;
      }
      if (next.kind === 'oauth-expired') {
        const msg =
          'Вход выполнен, но запрос на подключение устарел — начните подключение в приложении заново.';
        setError(msg);
        toast.error(msg);
        return;
      }
      await qc.invalidateQueries({ queryKey: ['me'] });
    } catch (e) {
      const msg = e instanceof Error ? `Network error: ${e.message}` : 'Network error';
      setError(msg);
      toast.error(msg);
    }
  }

  return (
    <div className="min-h-dvh grid place-items-center bg-[var(--color-surface-canvas)] text-[var(--color-text-primary)] p-6">
      <Card className="w-full max-w-[400px] p-8">
        <div className="mb-6">
          <Heading level={1} size="md">
            Sign in to WIT Admin
          </Heading>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            {subtitle ?? 'Use your admin email and password.'}
          </p>
        </div>
        <ZodForm
          schema={LoginSchema}
          defaultValues={{ email: '', password: '' }}
          onSubmit={handleSubmit}
        >
          <ZodField name="email" />
          <ZodField name="password" />
          {error ? <p className="text-sm text-[var(--color-state-danger-fg)]">{error}</p> : null}
          <Actions className="justify-stretch">
            <SubmitButton pendingLabel="Signing in…" className="w-full">
              Sign in
            </SubmitButton>
          </Actions>
        </ZodForm>
        <Separator className="my-6" />
        <p className="text-xs text-[var(--color-text-muted)] text-center">
          Need access? Contact your administrator.
        </p>
      </Card>
    </div>
  );
}
